import { useState, useRef, ReactNode } from 'react';
import styled from 'styled-components';
import { Typography, CircularProgress } from '@mui/material';
import { Flex } from '../Flex';
import Menu from '../Menu/Menu';
import { componentToken } from '../../style/utils';
import Chip, { ChipColor } from './Chip';

const ChipStyled = styled(Chip)<{ $clickable?: boolean; $readOnly?: boolean }>`
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  background-color: ${({ theme, $readOnly }) =>
    $readOnly
      ? 'transparent'
      : theme.tokens.components.chip.editable.backgroundColor};
  border-radius: ${componentToken('chip.editable.borderRadius')};
  border: 1px solid
    ${({ theme, $readOnly }) =>
      $readOnly ? 'transparent' : theme.tokens.components.chip.editable.border};
  transition: none;

  &:hover {
    background-color: ${({ theme, $clickable, $readOnly }) => {
      if ($clickable)
        return theme.tokens.components.chip.editable
          .clickableHoverBackgroundColor;
      return $readOnly
        ? 'transparent'
        : theme.tokens.components.chip.editable.hoverBackgroundColor;
    }};
  }
`;

const ChipContent = styled(Flex)`
  align-items: center;
  gap: ${componentToken('chip.chipContent.gap')};
`;

const IconWrapper = styled(Flex)`
  align-items: center;

  > svg {
    width: fit-content;
    height: 16px;
  }
`;

const LoadingWrapper = styled(Flex)`
  align-items: center;
  justify-content: center;
  min-width: 16px;
`;

const ChipLabel = ({
  icon,
  label,
  isLoading,
  endIcon,
}: {
  icon?: ReactNode;
  label: ReactNode;
  isLoading?: boolean;
  endIcon?: ReactNode;
}) => (
  <ChipContent>
    {icon && <IconWrapper>{icon}</IconWrapper>}
    {typeof label === 'string' ? (
      <Typography sx={{ fontSize: 'inherit' }}>{label}</Typography>
    ) : (
      label
    )}
    {isLoading && (
      <LoadingWrapper>
        <CircularProgress size={12} />
      </LoadingWrapper>
    )}
    {endIcon && <IconWrapper>{endIcon}</IconWrapper>}
  </ChipContent>
);

type ActionChipProps = {
  icon?: ReactNode;
  label: ReactNode;
  isLoading?: boolean;
  onClick?: (event: React.MouseEvent<HTMLElement>) => void;
  disabled?: boolean;
  readOnly?: boolean;
  size?: 'small' | 'medium';
  color?: ChipColor;
  endIcon?: ReactNode;
};

export const ActionChip = ({
  icon,
  label,
  isLoading,
  onClick,
  disabled,
  readOnly,
  size,
  color,
  endIcon,
}: ActionChipProps) => (
  <ChipStyled
    label={
      <ChipLabel
        icon={icon}
        label={label}
        isLoading={isLoading}
        endIcon={endIcon}
      />
    }
    onClick={readOnly ? undefined : onClick}
    disabled={disabled}
    $clickable={!disabled && !isLoading && !readOnly}
    $readOnly={readOnly}
    size={size}
    color={color}
  />
);

export type EditableChipOption = {
  value: string;
  label: string;
  icon?: JSX.Element;
  color?: ChipColor;
};

type SharedProps = {
  options: EditableChipOption[];
  icon?: ReactNode;
  endIcon?: ReactNode;
  disabled?: boolean;
  readOnly?: boolean;
  size?: 'small' | 'medium';
  color?: ChipColor;
  isLoading?: boolean;
  showSearch?: boolean;
};

type SingleSelectProps = SharedProps & {
  multiple?: false;
  value: string;
  onSave: (value: string) => void | Promise<void>;
};

type MultiSelectProps = SharedProps & {
  multiple: true;
  value: string[];
  onSave: (values: string[]) => void | Promise<void>;
  renderLabel?: (selectedValues: string[]) => ReactNode;
};

export type EditableChipProps = SingleSelectProps | MultiSelectProps;

const EditableChipSingle = ({
  value,
  options,
  icon,
  endIcon,
  onSave,
  disabled = false,
  readOnly = false,
  size,
  color,
  isLoading = false,
  showSearch = false,
}: SingleSelectProps) => {
  const [isSaving, setIsSaving] = useState(false);

  const selectedOption = options.find((opt) => opt.value === value);
  const displayLabel = selectedOption?.label ?? value;
  const displayIcon = icon ?? selectedOption?.icon;
  const displayColor = color ?? selectedOption?.color;

  const menuOptions = options.map((opt) => ({
    value: opt.value,
    label: opt.label,
    icon: opt.icon,
  }));

  const handleSelect = async (optionValue: string) => {
    if (optionValue === value) {
      return;
    }

    setIsSaving(true);

    try {
      await onSave(optionValue);
    } finally {
      setIsSaving(false);
    }
  };

  const isProcessing = isLoading || isSaving;

  if (readOnly) {
    return (
      <ActionChip
        icon={displayIcon}
        label={displayLabel}
        size={size}
        color={displayColor}
        endIcon={endIcon}
        readOnly
      />
    );
  }

  return (
    <Menu
      menuButton={
        <ActionChip
          icon={displayIcon}
          label={displayLabel}
          isLoading={isProcessing}
          disabled={disabled}
          size={size}
          color={displayColor}
          endIcon={endIcon}
        />
      }
      options={menuOptions}
      selectedOptions={[value]}
      onChange={handleSelect}
      showSearch={showSearch}
      closeOnChange
    />
  );
};

const EditableChipMultiple = ({
  value,
  options,
  icon,
  endIcon,
  onSave,
  disabled = false,
  readOnly = false,
  size,
  color,
  isLoading = false,
  showSearch = false,
  renderLabel,
}: MultiSelectProps) => {
  const [isSaving, setIsSaving] = useState(false);
  const [pendingValues, setPendingValues] = useState<string[]>(value);
  const pendingRef = useRef<string[]>(value);

  const menuOptions = options.map((opt) => ({
    value: opt.value,
    label: opt.label,
    icon: opt.icon,
  }));

  const handleOpen = () => {
    pendingRef.current = value;
    setPendingValues(value);
  };

  const handleChange = (newValues: string[]) => {
    pendingRef.current = newValues;
    setPendingValues(newValues);
  };

  const handleClose = async () => {
    const pending = pendingRef.current;
    const sortedPending = [...pending].sort();
    const sortedCurrent = [...value].sort();
    if (JSON.stringify(sortedPending) !== JSON.stringify(sortedCurrent)) {
      setIsSaving(true);
      try {
        await onSave(pending);
      } finally {
        setIsSaving(false);
      }
    }
  };

  const displayLabel: ReactNode = renderLabel
    ? renderLabel(value)
    : value.length === 0
    ? 'None'
    : value.length === 1
    ? options.find((o) => o.value === value[0])?.label ?? value[0]
    : `${value.length} selected`;

  const isProcessing = isLoading || isSaving;

  if (readOnly) {
    return (
      <ActionChip
        icon={icon}
        label={displayLabel}
        size={size}
        color={color}
        endIcon={endIcon}
        readOnly
      />
    );
  }

  return (
    <Menu
      menuButton={
        <ActionChip
          icon={icon}
          label={displayLabel}
          isLoading={isProcessing}
          disabled={disabled}
          size={size}
          color={color}
          endIcon={endIcon}
        />
      }
      options={menuOptions}
      selectedOptions={pendingValues}
      multiple
      onChange={handleChange}
      showSearch={showSearch}
      onOpen={handleOpen}
      onClose={() => {
        handleClose();
      }}
    />
  );
};

const EditableChip = (props: EditableChipProps) => {
  if (props.multiple) {
    return <EditableChipMultiple {...props} />;
  }
  return <EditableChipSingle {...props} />;
};

export default EditableChip;
